import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock,
  Flag,
  MapPin,
  Network,
  Pause,
  Pencil,
  Play,
  Timer,
  Trophy,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useClubQuery, useSelectedClub } from '@/features/club';
import {
  buildNameMap,
  buildProfileNames,
  buildSeedMap,
  collectMemberIds,
  CourtIcon,
  formatDate,
  formatDurationMinutes,
  formatInstant,
  isCorrectionLocked,
  matchDurationMinutes,
  MatchStatusMark,
  isSuperTiebreakRow,
  needsTiebreak,
  nextMatchPreview,
  roundLabel,
  rulesOf,
  setsWonOf,
  confirmedSetWinner,
  shortTime,
  toScorePayload,
  toViewStatus,
  useBracketQuery,
  useCompetitionQuery,
  useCourtsQuery,
  useEnterScoreMutation,
  useMatchActionMutation,
  useMatchQuery,
  useParticipantsQuery,
  useWalkoverMutation,
  initialRows,
  validateScore,
  type ApiMatch,
  type ApiMatchStatus,
  type SetRowDraft,
} from '@/features/competition';
import { getUsersByIds, userKeys } from '@/features/user';
import { ApiError } from '@/shared/api/ApiError';
import { cn } from '@/shared/lib/cn';
import {
  Alert,
  Avatar,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  CardHeader,
  CheckboxCard,
  Input,
  MetaBar,
  PageHeader,
  SegmentedControl,
  Skeleton,
  useToast,
} from '@/shared/ui';

/** Ton etykiety statusu meczu. */
const STATUS_TONE: Record<ApiMatchStatus, 'brand' | 'success' | 'info' | 'warning' | 'danger' | 'neutral'> = {
  SCHEDULED: 'info',
  IN_PROGRESS: 'brand',
  SUSPENDED: 'warning',
  FINISHED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
  WALKOVER: 'warning',
};

/** Pojedynczy wiersz panelu informacji: ikona, etykieta, wartość. */
function InfoRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-fg-subtle mt-0.5 shrink-0 [&_svg]:size-4">{icon}</span>
      <div className="min-w-0">
        <dt className="text-fg-muted text-xs">{label}</dt>
        <dd className="text-fg-strong text-sm font-semibold">{value}</dd>
      </div>
    </div>
  );
}

/** Nagłówek strony gracza — awatar, nazwisko, rozstawienie i rozstrzygnięcie. */
function PlayerHeader({
  label,
  seed,
  outcome,
  reversed = false,
}: {
  label: string;
  seed: number | undefined;
  /** Rozstrzygnięcie meczu z perspektywy tej strony — także przy walkowerze. */
  outcome?: 'winner' | 'loser';
  reversed?: boolean;
}) {
  const { t } = useTranslation('tournament');

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-4',
        reversed ? 'flex-row-reverse text-right' : '',
      )}
    >
      <Avatar name={label} size="xl" colorful />
      <div className="min-w-0">
        <p className="text-fg-strong truncate text-xl font-extrabold tracking-[-0.02em]">{label}</p>
        <div className={cn('mt-1 flex flex-wrap items-center gap-1.5', reversed && 'justify-end')}>
          {seed !== undefined && (
            <Badge tone="neutral" size="sm">
              Seed {seed}
            </Badge>
          )}
          {outcome === 'winner' && (
            <Badge tone="success" size="sm">
              {t('matchResult.summary.winner')}
            </Badge>
          )}
          {outcome === 'loser' && (
            <Badge tone="neutral" size="sm">
              {t('matchResult.summary.loser')}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Ekran wprowadzania wyniku meczu — układ z makiety
 * `wprowadzanie_wyników_meczu.png`.
 *
 * Wynik wpisuje `POST /api/matches/{id}/score` (mecz zakończony — korekta
 * przez `PUT`, dostępna tylko dla organizatora), walkower `POST /{id}/walkover`,
 * a przerwę i wznowienie obsługują akcje statusu. Walidacja setów odzwierciedla
 * walidatory backendu, więc błędny wynik zatrzymujemy z polskim komunikatem
 * przed wysłaniem żądania.
 */
export function MatchResultPage() {
  const { competitionId = '', matchId = '' } = useParams<{
    competitionId: string;
    matchId: string;
  }>();

  const matchQuery = useMatchQuery(matchId);
  const competitionQuery = useCompetitionQuery(competitionId);

  const { t } = useTranslation('tournament');

  if (matchQuery.isError || competitionQuery.isError) {
    return (
      <>
        <PageHeader title={t('matchResult.title')} />
        <Alert tone="danger">{t('matchResult.loadError')}</Alert>
      </>
    );
  }

  if (!matchQuery.data || !competitionQuery.data) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <MatchResultView
      // Zmiana wyniku (np. po edycji) przebudowuje formularz od zera.
      key={`${matchQuery.data.id}-${matchQuery.data.scoreEnteredAt ?? ''}-${matchQuery.data.status}`}
      match={matchQuery.data}
      competitionId={competitionId}
      competitionName={competitionQuery.data.name}
      venueClubId={competitionQuery.data.venueClubId}
      bracketDrawn={competitionQuery.data.bracketSize !== null}
      backTab={
        competitionQuery.data.system === 'GROUP_PLAYOFF' &&
        (matchQuery.data.groupNumber === 0 || matchQuery.data.groupNumber === null)
          ? 'faza-finalowa'
          : 'drabinka'
      }
    />
  );
}

interface MatchResultViewProps {
  match: ApiMatch;
  competitionId: string;
  competitionName: string;
  /** Obiekt turnieju — awaryjne źródło, gdy mecz nie ma własnego klubu. */
  venueClubId: string | null;
  /** Czy rozgrywka ma drabinkę — warunek zapytania o nawigację i następny mecz. */
  bracketDrawn: boolean;
  /** Zakładka powrotu — mecz fazy finałowej GROUP_PLAYOFF wraca do „Fazy finałowej". */
  backTab: 'drabinka' | 'faza-finalowa';
}

/** Widok właściwy — stan formularza budowany od nowa dla każdej wersji meczu. */
function MatchResultView({
  match,
  competitionId,
  competitionName,
  venueClubId,
  bracketDrawn,
  backTab,
}: MatchResultViewProps) {
  const { t } = useTranslation('tournament');
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { clubId } = useSelectedClub();

  const participantsQuery = useParticipantsQuery(competitionId);
  const courtsQuery = useCourtsQuery(competitionId);
  const bracketQuery = useBracketQuery(
    competitionId,
    bracketDrawn && match.round !== null && match.bracketPosition !== null,
  );

  const enterScoreMutation = useEnterScoreMutation(match.id, competitionId, clubId);
  const walkoverMutation = useWalkoverMutation(match.id, competitionId, clubId);
  const actionMutation = useMatchActionMutation(match.id, competitionId, clubId);

  const [rows, setRows] = useState<SetRowDraft[]>(() => initialRows(match));
  const [walkoverMode, setWalkoverMode] = useState(false);
  const [walkoverSide, setWalkoverSide] = useState<'1' | '2'>('1');
  const [formError, setFormError] = useState<string | null>(null);
  // Wpisany wynik pokazujemy jako podsumowanie; formularz korekty dopiero na życzenie.
  const [editing, setEditing] = useState(false);

  const participants = useMemo(() => participantsQuery.data ?? [], [participantsQuery.data]);
  const memberIds = useMemo(() => collectMemberIds(participants).sort(), [participants]);
  const profilesQuery = useQuery({
    queryKey: [...userKeys.all, 'batch', memberIds.join(',')],
    queryFn: () => getUsersByIds(memberIds),
    enabled: memberIds.length > 0,
  });
  const fullNames = useMemo(() => buildProfileNames(profilesQuery.data ?? []), [profilesQuery.data]);
  const shortNames = useMemo(() => buildNameMap(profilesQuery.data ?? []), [profilesQuery.data]);
  const seeds = useMemo(() => buildSeedMap(participants), [participants]);

  const rules = rulesOf(match.scoringRules);
  const discipline = match.discipline;
  const pointsGame = discipline === 'SQUASH' || discipline === 'BADMINTON';

  const side1Full = match.team1Ids.map((id) => fullNames.get(id) ?? '…').join(' / ') || '—';
  const side2Full = match.team2Ids.map((id) => fullNames.get(id) ?? '…').join(' / ') || '—';
  const side1Short = match.team1Ids.map((id) => shortNames.get(id) ?? '…').join(' / ') || '—';
  const side2Short = match.team2Ids.map((id) => shortNames.get(id) ?? '…').join(' / ') || '—';
  const seed1 = match.team1Ids.map((id) => seeds.get(id)).find((value) => value !== undefined);
  const seed2 = match.team2Ids.map((id) => seeds.get(id)).find((value) => value !== undefined);

  const court = (courtsQuery.data ?? []).find((item) => item.id === match.courtId);
  const courtLabel = court
    ? court.name || t('matchResult.court', { number: court.number })
    : null;

  // Obiekt: klub przypisany do meczu, a bez niego — obiekt całego turnieju.
  const venueQuery = useClubQuery(match.clubId ?? venueClubId ?? undefined);
  const venueLabel = venueQuery.data
    ? [venueQuery.data.name, venueQuery.data.city].filter(Boolean).join(', ')
    : null;

  // Czas trwania: start → koniec gry (endedAt z „Zakończ mecz"); dla starszych
  // meczów bez endedAt zapasowo moment wpisania wyniku.
  const durationMinutes = matchDurationMinutes(
    match.startedAt,
    match.endedAt ?? match.scoreEnteredAt,
  );

  // Etykieta fazy: runda drabinki albo grupa + kolejka.
  const phaseLabel =
    match.groupNumber !== null
      ? match.groupNumber === 0
        ? t('matchResult.finalGroup')
        : t('matchResult.group', { number: match.groupNumber })
      : roundLabel(match.round);

  const nextMatch = useMemo(
    () => (bracketQuery.data ? nextMatchPreview(bracketQuery.data, match, shortNames) : null),
    [bracketQuery.data, match, shortNames],
  );

  // Korekta zablokowana, gdy kolejna runda (albo mecz o 3. miejsce) już ruszyła —
  // backend i tak odrzuciłby zmianę zwycięzcy z 409.
  const correctionLocked = useMemo(
    () => (bracketQuery.data ? isCorrectionLocked(bracketQuery.data, match) : false),
    [bracketQuery.data, match],
  );

  const incompleteTeams = match.team1Ids.length === 0 || match.team2Ids.length === 0;
  const isCompleted = match.status === 'COMPLETED';
  const isWalkover = match.status === 'WALKOVER';
  // Walkower jest edytowalny jak zwykły wynik — można zmienić stronę albo wpisać
  // sety, gdy mecz jednak się odbył. Blokuje tylko anulowanie i niepełna obsada:
  // walkower przyznany bez przeciwnika backend i tak odrzuca (409).
  const readOnly = match.status === 'CANCELLED' || incompleteTeams;
  const formDisabled = readOnly || walkoverMode;

  // Rozstrzygnięcie meczu — działa też dla walkowera (setScores puste, result ustawiony).
  const winnerSide: 1 | 2 | null =
    match.result === 'PLAYER1_WIN' ? 1 : match.result === 'PLAYER2_WIN' ? 2 : null;
  const resolved = (isCompleted || isWalkover) && winnerSide !== null;
  // Formularz: mecz nierozstrzygnięty (wpisywanie) albo jawnie włączona korekta.
  // `resolved`, nie `isCompleted` — walkower też jest rozstrzygnięciem do poprawy.
  const showForm = !readOnly && (!resolved || editing);

  const [won1, won2] = setsWonOf(rows, rules, discipline);
  // W podsumowaniu liczymy z zapisanych setów, nie ze stanu formularza.
  const savedWon1 = match.setScores.filter((set) => set.score1 > set.score2).length;
  const savedWon2 = match.setScores.filter((set) => set.score2 > set.score1).length;

  /** Komunikat błędu z API albo tekst zastępczy. */
  function describe(error: unknown, fallback: string): string {
    return error instanceof ApiError ? error.message : fallback;
  }

  function updateRow(index: number, patch: Partial<SetRowDraft>) {
    setFormError(null);
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  }

  function handleSubmit() {
    if (walkoverMode) {
      walkoverMutation.mutate(
        { winningSide: walkoverSide === '1' ? 1 : 2, edit: resolved },
        {
          onSuccess: () => {
            showToast({
              tone: 'success',
              description: resolved
                ? t('matchResult.toast.walkoverUpdated')
                : t('matchResult.toast.walkoverSet'),
            });
            void navigate(`/turnieje/${competitionId}/${backTab}`);
          },
          onError: (error) =>
            showToast({
              tone: 'danger',
              description: describe(error, t('matchResult.toast.walkoverFailed')),
            }),
        },
      );
      return;
    }

    const problem = validateScore(rows, rules, discipline);
    if (problem) {
      setFormError(problem);
      return;
    }

    enterScoreMutation.mutate(
      // edit: również dla walkowera — PUT /score zamienia go na rozegrany mecz.
      { scores: toScorePayload(rows), edit: resolved },
      {
        onSuccess: () => {
          showToast({ tone: 'success', description: t('matchResult.toast.scoreSaved') });
          void navigate(`/turnieje/${competitionId}/${backTab}`);
        },
        onError: (error) =>
          showToast({
            tone: 'danger',
            description: describe(error, t('matchResult.toast.scoreFailed')),
          }),
      },
    );
  }

  function runAction(action: 'start' | 'finish' | 'suspend' | 'resume', message: string) {
    actionMutation.mutate(action, {
      onSuccess: () => showToast({ tone: 'success', description: message }),
      onError: (error) =>
        showToast({
          tone: 'danger',
          description: describe(error, t('matchResult.toast.actionFailed')),
        }),
    });
  }

  const matchNoLabel = t('matchResult.matchNo', { number: match.matchNumber ?? '?' });
  const submitPending = enterScoreMutation.isPending || walkoverMutation.isPending;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Breadcrumbs
          items={[
            { label: competitionName, to: `/turnieje/${competitionId}/${backTab}` },
            ...(phaseLabel ? [{ label: phaseLabel }] : []),
            { label: matchNoLabel },
          ]}
        />
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<ArrowLeft className="size-4" />}
          onClick={() => void navigate(`/turnieje/${competitionId}/${backTab}`)}
        >
          {t('matchResult.backToTournament')}
        </Button>
      </div>

      <PageHeader
        title={
          isCompleted || isWalkover ? t('matchResult.titleCompleted') : t('matchResult.title')
        }
        description={
          resolved ? t('matchResult.descriptionCompleted') : t('matchResult.description')
        }
      />

      {/* Kontekst meczu: turniej, runda, termin, kort — widoczny od razu, bez
          szukania w bocznym panelu. */}
      <MetaBar
        items={[
          { icon: <Trophy />, label: t('matchResult.info.tournament'), value: competitionName },
          {
            icon: <Network />,
            label: t('matchResult.info.round'),
            value: phaseLabel ?? t('matchResult.info.empty'),
          },
          {
            icon: <CalendarDays />,
            label: t('matchResult.info.term'),
            value:
              [formatDate(match.scheduledDate), shortTime(match.scheduledTime)]
                .filter(Boolean)
                .join(', ') || t('matchResult.info.empty'),
          },
          {
            icon: <MapPin />,
            label: t('matchResult.info.venue'),
            value: venueLabel ?? t('matchResult.info.empty'),
          },
          {
            icon: <CourtIcon />,
            label: t('matchResult.info.court'),
            value: courtLabel ?? t('matchResult.info.empty'),
          },
        ]}
      />

      {match.status === 'WALKOVER' && <Alert tone="warning">{t('matchResult.state.walkover')}</Alert>}
      {match.status === 'FINISHED' && <Alert tone="warning">{t('matchResult.state.finished')}</Alert>}
      {match.status === 'CANCELLED' && <Alert tone="info">{t('matchResult.state.cancelled')}</Alert>}
      {incompleteTeams && match.status === 'SCHEDULED' && (
        <Alert tone="info">{t('matchResult.state.incomplete')}</Alert>
      )}

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_19rem]">
        {/* Karta główna meczu */}
        <Card padding="lg" className="flex flex-col gap-6">
          {/* Pasek nagłówka: kort, status, faza, LIVE */}
          <div className="border-border -mx-6 -mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-6 py-3">
            {courtLabel && (
              <span className="text-fg-strong inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.06em] uppercase">
                <CourtIcon className="size-4" />
                {courtLabel}
              </span>
            )}
            <span className="text-fg-muted flex-1 text-center text-xs font-bold tracking-[0.06em] uppercase">
              {phaseLabel ? `${phaseLabel} • ` : ''}
              {matchNoLabel}
            </span>
            {/* Znak statusu bierzemy z `MatchStatusMark` — ta sama fajka i ta sama
                pulsujaca kropka co w drabince i na listach meczow. */}
            <Badge tone={STATUS_TONE[match.status]}>
              <MatchStatusMark status={toViewStatus(match.status)} size="sm" />
              {match.status === 'IN_PROGRESS' ? 'LIVE' : t(`matchResult.status.${match.status}`)}
            </Badge>
          </div>

          {/* Gracze — po rozstrzygnięciu (także walkowerem) z oznaczeniem stron */}
          <div className="grid items-center gap-4 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
            <PlayerHeader
              label={side1Full}
              seed={seed1}
              {...(resolved ? { outcome: winnerSide === 1 ? ('winner' as const) : ('loser' as const) } : {})}
            />
            <span className="text-fg-strong hidden text-center text-2xl font-extrabold sm:block">
              VS
            </span>
            <PlayerHeader
              label={side2Full}
              seed={seed2}
              reversed
              {...(resolved ? { outcome: winnerSide === 2 ? ('winner' as const) : ('loser' as const) } : {})}
            />
          </div>

          {/* Baner rozstrzygnięcia — kto wygrał; przy walkowerze to jedyne źródło wyniku */}
          {resolved && !editing && (
            <div className="border-brand-200 bg-brand-subtle flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3">
              <Trophy aria-hidden="true" className="text-brand-700 size-6 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-fg-muted text-[0.6875rem] font-bold tracking-[0.08em] uppercase">
                  {t('matchResult.summary.winner')}
                </p>
                <p className="text-fg-strong truncate text-base font-extrabold">
                  {winnerSide === 1 ? side1Full : side2Full}
                </p>
              </div>
              {isWalkover ? (
                <Badge tone="warning">{t('matchResult.status.WALKOVER')}</Badge>
              ) : (
                <span className="text-fg-strong text-2xl font-extrabold tabular-nums">
                  {savedWon1} : {savedWon2}
                </span>
              )}
            </div>
          )}

          {/* Zapisany wynik — statyczna tabela zamiast formularza */}
          {!showForm && isCompleted && match.setScores.length > 0 && (
            <div className="border-border overflow-hidden rounded-xl border">
              <div className="bg-surface-sunken text-fg-muted grid grid-cols-[3.5rem_2.5rem_minmax(0,1fr)_1.5rem_minmax(0,1fr)] items-center gap-2 px-3 py-2.5 text-[0.6875rem] font-bold tracking-[0.08em] uppercase">
                <span>{pointsGame ? t('matchResult.table.game') : t('matchResult.table.set')}</span>
                <span />
                <span className="text-center">{side1Short}</span>
                <span />
                <span className="text-center">{side2Short}</span>
              </div>

              {match.setScores.map((set) => (
                <div
                  key={set.setNumber}
                  className="border-border grid grid-cols-[3.5rem_2.5rem_minmax(0,1fr)_1.5rem_minmax(0,1fr)] items-center gap-2 border-t px-3 py-2.5"
                >
                  <span className="text-fg-strong text-sm font-bold tabular-nums">
                    {set.setNumber}
                  </span>
                  <span>
                    <CheckCircle2 aria-hidden="true" className="text-success-500 size-4.5" />
                  </span>
                  {[1, 2].map((side) => {
                    const value = side === 1 ? set.score1 : set.score2;
                    const tiebreak = side === 1 ? set.tiebreakScore1 : set.tiebreakScore2;
                    const wonSet = side === 1 ? set.score1 > set.score2 : set.score2 > set.score1;
                    return (
                      <span
                        key={side}
                        className={cn(
                          'text-center text-lg tabular-nums',
                          side === 2 && 'col-start-5',
                          wonSet ? 'text-fg-strong font-extrabold' : 'text-fg-muted',
                        )}
                      >
                        {value}
                        {tiebreak !== null && (
                          <sup className="text-fg-subtle ml-0.5 text-[0.625rem] font-normal">
                            {tiebreak}
                          </sup>
                        )}
                      </span>
                    );
                  })}
                  <span className="text-fg-subtle col-start-4 row-start-1 text-center">–</span>
                </div>
              ))}

              <div className="bg-surface-sunken border-border grid grid-cols-[6rem_minmax(0,1fr)_1.5rem_minmax(0,1fr)] items-center gap-2 border-t px-3 py-3">
                <span className="text-fg-muted text-[0.6875rem] font-bold tracking-[0.08em] uppercase">
                  {t('matchResult.table.final')}
                </span>
                <span className="text-fg-strong text-center text-2xl font-extrabold tabular-nums">
                  {savedWon1}
                </span>
                <span className="text-fg-muted text-center text-xl font-bold">:</span>
                <span className="text-fg-strong text-center text-2xl font-extrabold tabular-nums">
                  {savedWon2}
                </span>
              </div>
            </div>
          )}

          {/* Tabela setów — formularz wpisywania / korekty */}
          {showForm && (
          <div className="border-border overflow-hidden rounded-xl border">
            <div className="bg-surface-sunken text-fg-muted grid grid-cols-[3.5rem_2.5rem_minmax(0,1fr)_1.5rem_minmax(0,1fr)] items-center gap-2 px-3 py-2.5 text-[0.6875rem] font-bold tracking-[0.08em] uppercase">
              <span>{pointsGame ? t('matchResult.table.game') : t('matchResult.table.set')}</span>
              <span />
              <span className="text-center">{side1Short}</span>
              <span />
              <span className="text-center">{side2Short}</span>
            </div>

            {rows.map((row, index) => {
              const winner = confirmedSetWinner(rows, index, rules, discipline);
              const superTb = isSuperTiebreakRow(rows, index, rules) && !pointsGame;
              const showTiebreak = !pointsGame && needsTiebreak(row, rules, discipline);

              return (
                <div
                  key={index}
                  className="border-border grid grid-cols-[3.5rem_2.5rem_minmax(0,1fr)_1.5rem_minmax(0,1fr)] items-center gap-2 border-t px-3 py-2.5"
                >
                  <span className="text-fg-strong text-sm font-bold tabular-nums">
                    {index + 1}
                    {superTb && <span className="text-fg-subtle ml-1 text-[0.625rem]">STB</span>}
                  </span>
                  <span>
                    {winner !== null && (
                      <CheckCircle2 aria-hidden="true" className="text-success-500 size-4.5" />
                    )}
                  </span>

                  {[1, 2].map((side) => (
                    <div
                      key={side}
                      className={cn(
                        'flex items-center justify-center gap-1.5',
                        side === 2 && 'col-start-5',
                      )}
                    >
                      <Input
                        type="number"
                        min={0}
                        inputSize="sm"
                        disabled={formDisabled}
                        aria-label={`${pointsGame ? t('matchResult.table.game') : t('matchResult.table.set')} ${index + 1} — ${side === 1 ? side1Short : side2Short}`}
                        value={side === 1 ? row.score1 : row.score2}
                        onChange={(event) =>
                          updateRow(index, side === 1 ? { score1: event.target.value } : { score2: event.target.value })
                        }
                        className={cn(
                          'w-16 text-center font-bold tabular-nums',
                          winner === side && 'border-brand ring-brand/25 ring-2',
                        )}
                      />
                      {showTiebreak && (
                        <Input
                          type="number"
                          min={0}
                          inputSize="sm"
                          disabled={formDisabled}
                          aria-label={`${t('matchResult.table.tiebreak')} — ${side === 1 ? side1Short : side2Short}`}
                          placeholder="TB"
                          value={side === 1 ? row.tiebreak1 : row.tiebreak2}
                          onChange={(event) =>
                            updateRow(
                              index,
                              side === 1
                                ? { tiebreak1: event.target.value }
                                : { tiebreak2: event.target.value },
                            )
                          }
                          // Ta sama szerokosc co pole seta — w wezszym polu
                          // cyfra tie-breaka byla przycieta i nieczytelna.
                          className="w-16 text-center font-bold tabular-nums"
                        />
                      )}
                    </div>
                  ))}

                  <span className="text-fg-subtle col-start-4 row-start-1 text-center">–</span>
                </div>
              );
            })}

            {/* Wynik końcowy w setach */}
            <div className="bg-surface-sunken border-border grid grid-cols-[6rem_minmax(0,1fr)_1.5rem_minmax(0,1fr)] items-center gap-2 border-t px-3 py-3">
              <span className="text-fg-muted text-[0.6875rem] font-bold tracking-[0.08em] uppercase">
                {t('matchResult.table.final')}
              </span>
              <span className="text-fg-strong text-center text-2xl font-extrabold tabular-nums">
                {won1}
              </span>
              <span className="text-fg-muted text-center text-xl font-bold">:</span>
              <span className="text-fg-strong text-center text-2xl font-extrabold tabular-nums">
                {won2}
              </span>
            </div>
          </div>
          )}

          {showForm && rules.superTiebreak && !pointsGame && (
            <p className="text-fg-subtle -mt-3 text-xs">{t('matchResult.table.superTiebreakHint')}</p>
          )}

          {showForm && formError && <Alert tone="warning">{formError}</Alert>}

          {/* Walkower — alternatywa dla wpisywanego wyniku, dostępna też przy
              korekcie: pozwala zamienić rozegrany mecz na walkower i odwrotnie. */}
          {showForm && (
            <div className="flex flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <CheckboxCard
                  checked={walkoverMode}
                  onCheckedChange={(checked) => {
                    setWalkoverMode(checked);
                    setFormError(null);
                  }}
                  title={t('matchResult.walkover.title')}
                  description={t('matchResult.walkover.hint')}
                />
                {walkoverMode && (
                  <div className="flex flex-col justify-center gap-2">
                    <span className="text-fg-muted text-xs font-semibold">
                      {t('matchResult.walkover.who')}
                    </span>
                    <SegmentedControl
                      label={t('matchResult.walkover.who')}
                      size="sm"
                      fullWidth
                      value={walkoverSide}
                      onValueChange={setWalkoverSide}
                      options={[
                        { value: '1', label: side1Short },
                        { value: '2', label: side2Short },
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stopka akcji — formularz wpisywania / korekty */}
          {showForm && (
            <div className="border-border -mx-6 -mb-6 flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4">
              <Button
                variant="secondary"
                onClick={() => {
                  // Anulowanie korekty wraca do podsumowania; przy wpisywaniu — do turnieju.
                  if (editing) {
                    setRows(initialRows(match));
                    setFormError(null);
                    setWalkoverMode(false);
                    setEditing(false);
                  } else {
                    void navigate(`/turnieje/${competitionId}/${backTab}`);
                  }
                }}
              >
                {t('matchResult.actions.cancel')}
              </Button>
              <div className="flex flex-col items-end gap-1.5">
                <Button
                  size="lg"
                  trailingIcon={<Check className="size-4" />}
                  loading={submitPending}
                  onClick={handleSubmit}
                >
                  {walkoverMode
                    ? resolved
                      ? t('matchResult.walkover.submitEdit')
                      : t('matchResult.walkover.submit')
                    : resolved
                      ? t('matchResult.actions.submitEdit')
                      : t('matchResult.actions.submit')}
                </Button>
                <span className="text-fg-subtle text-xs">
                  {t('matchResult.actions.submitHint')}
                </span>
              </div>
            </div>
          )}

          {/* Stopka podsumowania — korekta wyniku na życzenie, o ile drabinka
              za tym meczem jeszcze nie ruszyła */}
          {resolved && !editing && (
            <div className="border-border -mx-6 -mb-6 flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4">
              {correctionLocked ? (
                <span className="text-fg-muted text-sm">
                  {t('matchResult.summary.editLocked')}
                </span>
              ) : (
                <>
                  <span className="text-fg-subtle text-xs">
                    {t('matchResult.summary.editHint')}
                  </span>
                  <Button
                    variant="secondary"
                    leadingIcon={<Pencil className="size-4" />}
                    onClick={() => {
                      setRows(initialRows(match));
                      setFormError(null);
                      // Korekta walkowera startuje z zaznaczonym walkowerem i obecnym
                      // zwycięzcą — odznaczenie pozwala wpisać wynik rozegranego meczu.
                      setWalkoverMode(isWalkover);
                      setWalkoverSide(winnerSide === 2 ? '2' : '1');
                      setEditing(true);
                    }}
                  >
                    {t('matchResult.summary.edit')}
                  </Button>
                </>
              )}
            </div>
          )}
        </Card>

        {/* Kolumna prawa */}
        <div className="flex flex-col gap-4 xl:sticky xl:top-6">
          <Card padding="lg">
            <CardHeader title={t('matchResult.info.title')} />
            <dl className="flex flex-col gap-4">
              {/* Termin i kort są w pasku meta pod nagłówkiem — tu tylko przebieg. */}
              <InfoRow
                icon={<Clock />}
                label={t('matchResult.info.started')}
                value={formatInstant(match.startedAt) ?? t('matchResult.info.empty')}
              />
              {match.scoreEnteredAt && (
                <InfoRow
                  icon={<CheckCircle2 />}
                  label={t('matchResult.info.scoreEntered')}
                  value={formatInstant(match.scoreEnteredAt) ?? t('matchResult.info.empty')}
                />
              )}
              {durationMinutes !== null && (
                <InfoRow
                  icon={<Timer />}
                  label={t('matchResult.info.duration')}
                  value={formatDurationMinutes(durationMinutes)}
                />
              )}
            </dl>

            {/* Akcje statusu — start / przerwa / wznowienie */}
            {!incompleteTeams && (
              <div className="border-border mt-4 flex flex-wrap gap-2 border-t pt-4">
                {match.status === 'SCHEDULED' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    leadingIcon={<Play className="size-4" />}
                    loading={actionMutation.isPending}
                    onClick={() => runAction('start', t('matchResult.toast.started'))}
                  >
                    {t('matchResult.actions.start')}
                  </Button>
                )}
                {match.status === 'IN_PROGRESS' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    leadingIcon={<Pause className="size-4" />}
                    loading={actionMutation.isPending}
                    onClick={() => runAction('suspend', t('matchResult.toast.suspended'))}
                  >
                    {t('matchResult.actions.suspend')}
                  </Button>
                )}
                {match.status === 'SUSPENDED' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    leadingIcon={<Play className="size-4" />}
                    loading={actionMutation.isPending}
                    onClick={() => runAction('resume', t('matchResult.toast.resumed'))}
                  >
                    {t('matchResult.actions.resume')}
                  </Button>
                )}
                {/* Koniec gry bez wyniku — mecz przechodzi w FINISHED i czeka
                    na rezultat; endedAt domyka pomiar czasu. */}
                {(match.status === 'IN_PROGRESS' || match.status === 'SUSPENDED') && (
                  <Button
                    size="sm"
                    variant="secondary"
                    leadingIcon={<Flag className="size-4" />}
                    loading={actionMutation.isPending}
                    onClick={() => runAction('finish', t('matchResult.toast.finished'))}
                  >
                    {t('matchResult.actions.finish')}
                  </Button>
                )}
              </div>
            )}
          </Card>

          {nextMatch && (
            <Card padding="lg" className="border-brand-200 bg-brand-subtle">
              <div className="flex items-start gap-3">
                <Trophy aria-hidden="true" className="text-brand-700 size-6 shrink-0" />
                <div className="flex flex-col gap-1">
                  <h3 className="text-fg-strong text-[0.6875rem] font-bold tracking-[0.08em] uppercase">
                    {t('matchResult.next.title')}
                  </h3>
                  <p className="text-fg text-sm">
                    {t('matchResult.next.round', { round: roundLabel(nextMatch.round) })}{' '}
                    {nextMatch.opponentLabel
                      ? t('matchResult.next.opponent', { opponent: nextMatch.opponentLabel })
                      : t('matchResult.next.opponentUnknown')}
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
